import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listBillings, listProjects, createProject, updateProject, listCanvasLibrary, updateServiceRequest } from '../lib/adminApi';
import { CANVAS_REGISTRY } from '../canvases/registry';
import { COUNTRIES } from '../data/countries';
import { evaluateSchedule } from '../lib/timezone';
import { campaignUrl, proposeDomain } from '../lib/siteUrl';
import ConfirmDialog from '../components/ConfirmDialog';

function StatusBadge({ status }) {
  return <span className={`admin-badge admin-badge-${status}`}>{status}</span>;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function Projects() {
  const [view, setView] = useState('list'); // 'list' | 'form' | 'detail' | 'created'
  const [projects, setProjects] = useState([]);
  const [billings, setBillings] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [created, setCreated] = useState(null);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  const [billingId, setBillingId] = useState('');
  const [canvasType, setCanvasType] = useState('');
  const [canvasLibraryId, setCanvasLibraryId] = useState('');
  const [domainName, setDomainName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  const wizard = Boolean(location.state?.wizard);
  const wizardBillingId = location.state?.billingId;
  const pendingRequest = location.state?.pendingRequest || null;

  const load = () => {
    setLoading(true);
    Promise.all([listProjects(), listBillings(), listCanvasLibrary()])
      .then(([p, b, c]) => {
        setProjects(p.projects || []);
        setBillings(b.billings || []);
        setLibrary(c.canvases || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (wizard && wizardBillingId) {
      setBillingId(wizardBillingId);
      setDomainName(proposeDomain());
      setView('form');
      if (pendingRequest) {
        setStartDate(pendingRequest.start_date || '');
        setEndDate(pendingRequest.end_date || '');
        const libId = pendingRequest.canvas_library_id || pendingRequest.canvas_library?.id || '';
        const type = pendingRequest.moving_object_type || pendingRequest.canvas_library?.moving_object_type || '';
        setCanvasLibraryId(libId);
        setCanvasType(type);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedBilling = useMemo(
    () => billings.find((b) => b.id === billingId) || null,
    [billings, billingId]
  );

  const timezone = useMemo(() => {
    if (!selectedBilling) return null;
    const country = COUNTRIES.find((c) => c.name === selectedBilling.customer_country);
    return country ? country.timezone : 'UTC';
  }, [selectedBilling]);

  const schedule = useMemo(() => {
    if (!startDate || !endDate || !timezone) return null;
    return evaluateSchedule(startDate, endDate, timezone);
  }, [startDate, endDate, timezone]);

  const canvasOptions = library.length
    ? library
    : CANVAS_REGISTRY.map((c) => ({
        id: c.key,
        moving_object_type: c.key,
        label: c.label,
      }));

  const canvasLabel = (project) => {
    const fromLibrary = library.find((c) => c.id === project.canvas_library_id);
    if (fromLibrary?.label) return fromLibrary.label;
    return CANVAS_REGISTRY.find((c) => c.key === project.canvas_type)?.label || project.canvas_type;
  };

  const canSave = Boolean(billingId && canvasType && domainName && startDate && endDate && schedule && !schedule.error);

  const resetForm = () => {
    setBillingId('');
    setCanvasType('');
    setCanvasLibraryId('');
    setDomainName(proposeDomain());
    setStartDate('');
    setEndDate('');
    setError('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSave) return;
    setSaving(true);
    try {
      const result = await createProject({
        billing_id: billingId,
        canvas_type: canvasType,
        canvas_library_id: canvasLibraryId || null,
        domain_name: domainName,
        start_date: startDate,
        end_date: endDate,
      });
      if (pendingRequest?.id) {
        await updateServiceRequest(pendingRequest.id, {
          project_id: result.project.id,
          status: 'activated',
        });
      }
      setCreated(result.project);
      resetForm();
      setView('created');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (project) => {
    setSelected(project);
    setView('detail');
  };

  const handleToggleQr = async (project) => {
    try {
      const result = await updateProject(project.id, { is_qr_visible: !project.is_qr_visible });
      setProjects((rows) => rows.map((r) => (r.id === project.id ? result.project : r)));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeactivate = async (project) => {
    setConfirmingDeactivate(false);
    try {
      const result = await updateProject(project.id, { deactivate: true });
      setSelected(result.project);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (view === 'created' && created) {
    return (
      <div>
        <div className="admin-page-header">
          <div>
            <h1>{wizard ? 'Service Created' : 'Project Created'}</h1>
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-alert admin-alert-success">
            Saved. Share the activation code below with the customer.
          </div>
          <div className="admin-detail-grid" style={{ marginTop: 12 }}>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Weblink</div>
              <div className="admin-detail-value">
                <a href={campaignUrl(created.domain_name)} target="_blank" rel="noreferrer">
                  {campaignUrl(created.domain_name)}
                </a>
              </div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Activation Code</div>
              <div className="admin-detail-value"><span className="admin-code">{created.activation_code}</span></div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Status</div>
              <div className="admin-detail-value"><StatusBadge status={created.status} /></div>
            </div>
          </div>
          <div className="admin-form-actions" style={{ marginTop: 20 }}>
            <button
              className="admin-btn admin-btn-primary"
              onClick={() => (wizard ? navigate('/admin/services') : setView('list'))}
            >
              {wizard ? 'Go to Services' : 'Back to Project Homepage'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'detail' && selected) {
    return (
      <div>
        <button className="admin-back-link" onClick={() => { setView('list'); setSelected(null); }}>
          Back to Project Homepage
        </button>
        <div className="admin-page-header">
          <div>
            <h1>
              <a href={campaignUrl(selected.domain_name)} target="_blank" rel="noreferrer">
                {campaignUrl(selected.domain_name)}
              </a>
            </h1>
            <p>Canvas: {canvasLabel(selected)}</p>
          </div>
          <StatusBadge status={selected.status} />
        </div>

        {error && <div className="admin-alert admin-alert-error">{error}</div>}

        <div className="admin-card">
          <div className="admin-detail-grid">
            <div className="admin-detail-item">
              <div className="admin-detail-label">Activation Code</div>
              <div className="admin-detail-value"><span className="admin-code">{selected.activation_code}</span></div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Activated by customer</div>
              <div className="admin-detail-value">{selected.is_activated ? 'Yes' : 'Not yet'}</div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Start Date</div>
              <div className="admin-detail-value">{new Date(selected.start_date).toLocaleString()}</div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">End Date (effective)</div>
              <div className="admin-detail-value">{new Date(selected.end_date).toLocaleString()}</div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">QR Code Display</div>
              <div className="admin-detail-value">
                <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => handleToggleQr(selected)}>
                  {selected.is_qr_visible ? 'Showing — turn off' : 'Hidden — turn on'}
                </button>
              </div>
            </div>
          </div>

          <div className="admin-form-actions" style={{ marginTop: 20 }}>
            <button
              className="admin-btn admin-btn-danger"
              disabled={selected.status === 'expired'}
              onClick={() => setConfirmingDeactivate(true)}
            >
              {selected.status === 'expired' ? 'Already deactivated' : 'Deactivate Service'}
            </button>
          </div>
        </div>

        <ConfirmDialog
          open={confirmingDeactivate}
          title="Deactivate this service?"
          message="This ends the service immediately — the campaign page stops accepting new messages right away. This cannot be undone."
          confirmLabel="Yes, deactivate"
          cancelLabel="No"
          onConfirm={() => handleDeactivate(selected)}
          onCancel={() => setConfirmingDeactivate(false)}
        />
      </div>
    );
  }

  if (view === 'form') {
    return (
      <div>
        {!wizard && (
          <button className="admin-back-link" onClick={() => { setView('list'); resetForm(); }}>
            Back to Project Homepage
          </button>
        )}
        <div className="admin-page-header">
          <div>
            <h1>{wizard ? 'Create New Service — Step 2 of 2' : 'New Project'}</h1>
            <p>Canvas, domain, activation code and the active date range.</p>
          </div>
        </div>

        {wizard && (
          <div className="admin-steps">
            <div className="admin-step done">1. Billings and Customer ✓</div>
            <div className="admin-step active">2. Project</div>
          </div>
        )}

        <div className="admin-card">
          {error && <div className="admin-alert admin-alert-error">{error}</div>}
          {schedule?.error && <div className="admin-alert admin-alert-error">{schedule.error}</div>}

          <form className="admin-form" onSubmit={handleCreate}>
            {!wizard && (
              <div className="admin-field">
                <label>Linked Customer *</label>
                <select required value={billingId} onChange={(e) => setBillingId(e.target.value)}>
                  <option value="">Select a billing/customer record…</option>
                  {billings.map((b) => (
                    <option key={b.id} value={b.id}>{b.customer_name} — {b.customer_email}</option>
                  ))}
                </select>
                <span className="admin-hint">Dates below are interpreted in this customer's country timezone.</span>
              </div>
            )}

            <div className="admin-form-row">
              <div className="admin-field">
                <label>Canvas *</label>
                <select required value={canvasLibraryId || canvasType} onChange={(e) => {
                  const chosen = canvasOptions.find((c) => c.id === e.target.value);
                  if (!chosen) {
                    setCanvasLibraryId('');
                    setCanvasType('');
                    return;
                  }
                  setCanvasType(chosen.moving_object_type);
                  setCanvasLibraryId(chosen.id !== chosen.moving_object_type ? chosen.id : '');
                }}>
                  <option value="">Select a canvas…</option>
                  {canvasOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="admin-field">
                <label>Create Domain *</label>
                <input
                  required
                  value={domainName}
                  maxLength={64}
                  onChange={(e) => setDomainName(e.target.value.replace(/[^A-Za-z0-9]/g, ''))}
                  placeholder="16-character proposal"
                />
                <span className="admin-hint">
                  Auto-proposed 16-character mixed-case domain — edit before saving.{' '}
                  {campaignUrl(domainName || '{domain}')}
                </span>
              </div>
            </div>

            <div className="admin-form-row">
              <div className="admin-field">
                <label>Start Date *</label>
                <input type="date" required min={todayIso()} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <span className="admin-hint">Service starts 00:00:00 on this date{timezone ? ` (${timezone})` : ''}.</span>
              </div>
              <div className="admin-field">
                <label>End Date *</label>
                <input type="date" required min={startDate || todayIso()} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                <span className="admin-hint">Service ends 00:00:00 the day after this date.</span>
              </div>
            </div>

            <div className="admin-field">
              <label>Activation Code</label>
              <input disabled value="Generated automatically once saved" />
            </div>

            <div className="admin-form-actions" style={wizard ? { justifyContent: 'space-between' } : undefined}>
              {wizard && (
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => navigate('/admin/billings', { state: { wizard: true, pendingRequest, billingId } })}
                >
                  Back
                </button>
              )}
              <button type="submit" className="admin-btn admin-btn-primary" disabled={!canSave || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1>Projects</h1>
          <p>Canvas, domain, schedule and activation code for each service.</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => { resetForm(); setView('form'); }}>+ New Project</button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Canvas</th>
              <th>Status</th>
              <th>Start</th>
              <th>End</th>
              <th>QR</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id} className="clickable" onClick={() => openDetail(p)}>
                <td>{p.domain_name}</td>
                <td>{canvasLabel(p)}</td>
                <td><StatusBadge status={p.status} /></td>
                <td>{new Date(p.start_date).toLocaleDateString()}</td>
                <td>{new Date(p.end_date).toLocaleDateString()}</td>
                <td>{p.is_qr_visible ? 'On' : 'Off'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && projects.length === 0 && (
          <div className="admin-empty">No projects yet.</div>
        )}
      </div>
    </div>
  );
}
